import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import cvData from "../../data/cv";
import { reportError } from "../../monitoring/reportError";
import { GmailIcon } from "./gmailIconOptions";
import "./Contact.css";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID?.trim();
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID?.trim();
const EMAILJS_AUTOREPLY_TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID?.trim();
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY?.trim();

const emailJsSendOptions = () => ({ publicKey: EMAILJS_PUBLIC_KEY });

const phoneDigits = (phone) => phone.replace(/\D/g, "");

const DEFAULT_EMAIL_SUBJECT = "Enquiry from Pradeep Singh's portfolio";
const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 80;
const MESSAGE_MAX_LENGTH = 400;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const capitalizeFirstLetter = (value) => {
  if (!value) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const startsWithCapitalLetter = (value) => {
  const first = value.trim().charAt(0);
  return Boolean(first) && first === first.toUpperCase() && first !== first.toLowerCase();
};

const validateName = (value) => {
  const name = value.trim();
  if (!name) {
    return { valid: false, hint: "Enter your name." };
  }
  if (!startsWithCapitalLetter(name)) {
    return {
      valid: false,
      hint: "Name must start with a capital letter.",
    };
  }
  if (name.length < NAME_MIN_LENGTH) {
    return {
      valid: false,
      hint: `Name needs at least ${NAME_MIN_LENGTH} characters.`,
    };
  }
  if (name.length > NAME_MAX_LENGTH) {
    return {
      valid: false,
      hint: `Name must be ${NAME_MAX_LENGTH} characters or fewer.`,
    };
  }
  return { valid: true, hint: "" };
};

const validateEmail = (value) => {
  const email = value.trim();
  if (!email) {
    return { valid: false, hint: "Enter your email address." };
  }
  if (!EMAIL_PATTERN.test(email)) {
    return {
      valid: false,
      hint: "Enter a valid email, for example name@domain.com.",
    };
  }
  return { valid: true, hint: "" };
};

const validateMessage = (value) => {
  if (!value.trim()) {
    return { valid: false, hint: "Message cannot be empty." };
  }
  if (value.length > MESSAGE_MAX_LENGTH) {
    return {
      valid: false,
      hint: `Message must be ${MESSAGE_MAX_LENGTH} characters or fewer.`,
    };
  }
  return { valid: true, hint: "" };
};

const ContactSendFailure = () => (
  <div className="contact-send-failure" role="alert">
    <p className="contact-send-failure__title">Message could not be sent</p>
    <p className="contact-send-failure__text">
      Something went wrong while sending your message. Please reach out directly:
    </p>
    <div className="contact-send-failure__actions">
      <a
        className="contact-send-failure__action contact-send-failure__action--email"
        href={`mailto:${cvData.profile.email}`}
      >
        <i className="icon-mail6" aria-hidden="true" />
        <span>{cvData.profile.email}</span>
      </a>
      <a
        className="contact-send-failure__action contact-send-failure__action--whatsapp"
        href={`https://wa.me/${phoneDigits(cvData.profile.phone)}`}
        target="_blank"
        rel="noreferrer"
      >
        <i className="icon-whatsapp" aria-hidden="true" />
        <span>{cvData.profile.phone}</span>
      </a>
    </div>
  </div>
);

const logSendFailure = (error) => {
  reportError(error, {
    fatal: false,
    source: "contact.emailjs",
    params: {
      emailjs_status: error?.status,
      emailjs_detail: error?.text,
    },
  });
};

const sendAutoReply = (formValues) => {
  if (!EMAILJS_AUTOREPLY_TEMPLATE_ID) {
    return Promise.resolve();
  }

  return emailjs
    .send(
      EMAILJS_SERVICE_ID,
      EMAILJS_AUTOREPLY_TEMPLATE_ID,
      {
        from_name: formValues.from_name,
        to_email: formValues.from_email,
        to_name: formValues.from_name,
      },
      emailJsSendOptions()
    )
    .catch((error) => {
      reportError(error, {
        fatal: false,
        source: "contact.autoreply",
        params: {
          emailjs_status: error?.status,
          emailjs_detail: error?.text,
        },
      });
    });
};

const FieldFlag = ({ show, valid }) => {
  if (!show) {
    return null;
  }

  return (
    <span
      className={`contact-field-flag ${valid ? "is-valid" : "is-invalid"}`}
      aria-hidden="true"
    >
      {valid ? (
        <svg viewBox="0 0 16 16" width="16" height="16">
          <path
            d="M2.8 8.4 L6.4 12 L13.2 3.8"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" width="16" height="16">
          <path
            d="M4 4 L12 12 M12 4 L4 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </span>
  );
};

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const form = useRef();

  const syncFieldsFromDom = () => {
    const formEl = form.current;
    if (!formEl) {
      return;
    }

    const nameValue = formEl.elements.namedItem("from_name")?.value || "";
    const emailValue = formEl.elements.namedItem("from_email")?.value || "";

    setName((prev) => {
      const next = capitalizeFirstLetter(nameValue);
      return next === prev ? prev : next;
    });
    setEmail((prev) => (emailValue === prev ? prev : emailValue));
  };

  useEffect(() => {
    const timers = [0, 150, 400, 1000].map((delay) =>
      window.setTimeout(syncFieldsFromDom, delay)
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const handleAutofillAnimation = (event) => {
    if (event.animationName === "contact-autofill") {
      syncFieldsFromDom();
    }
  };

  const nameCheck = validateName(name);
  const emailCheck = validateEmail(email);
  const messageCheck = validateMessage(message);
  const isFormValid = nameCheck.valid && emailCheck.valid && messageCheck.valid;

  const showNameStatus = showAllErrors || name.length > 0;
  const showEmailStatus = showAllErrors || email.length > 0;
  const showMessageStatus = showAllErrors || message.length > 0;

  const sendEmail = (e) => {
    e.preventDefault();
    setSendError(null);

    if (!isFormValid) {
      setShowAllErrors(true);
      return;
    }

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setSendError("config");
      return;
    }

    setIsSending(true);

    const templateParams = {
      from_name: name.trim(),
      from_email: email.trim(),
      reply_to: email.trim(),
      message: message.trim(),
      subject: DEFAULT_EMAIL_SUBJECT,
    };

    emailjs
      .send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, emailJsSendOptions())
      .then(() => sendAutoReply(templateParams))
      .then(
        () => {
          setSubmitted(true);
          setIsSending(false);
        },
        (error) => {
          setIsSending(false);
          setSendError("failed");
          logSendFailure(error);
        }
      );
  };

  const renderFormPanel = () => {
    if (submitted) {
      return (
        <div>
          <h2>Message sent.</h2>
          <p>Thanks for reaching out. I'll get back to you shortly.</p>
        </div>
      );
    }

    if (sendError === "failed") {
      return <ContactSendFailure />;
    }

    if (sendError === "config") {
      return (
        <p className="contact-field-hint is-invalid contact-config-error">
          EmailJS env vars are missing. Copy .env.example to .env and restart the app.
        </p>
      );
    }

    return (
      <form
        ref={form}
        className="contact-form"
        onSubmit={sendEmail}
        onInput={syncFieldsFromDom}
        noValidate
      >
                    <input
                      type="hidden"
                      name="subject"
                      value={DEFAULT_EMAIL_SUBJECT}
                    />
                    <div
                      className={`form-group contact-field${
                        showNameStatus
                          ? nameCheck.valid
                            ? " is-valid"
                            : " is-invalid"
                          : ""
                      }`}
                    >
                      <div className="contact-field-control">
                        <input
                          type="text"
                          className="form-control"
                          name="from_name"
                          placeholder="Name"
                          value={name}
                          onChange={(event) =>
                            setName(capitalizeFirstLetter(event.target.value))
                          }
                          onBlur={syncFieldsFromDom}
                          onAnimationStart={handleAutofillAnimation}
                          maxLength={NAME_MAX_LENGTH}
                          autoComplete="name"
                          aria-invalid={showNameStatus && !nameCheck.valid}
                          aria-describedby="contact-name-hint"
                        />
                        <FieldFlag show={showNameStatus} valid={nameCheck.valid} />
                      </div>
                      {showNameStatus && !nameCheck.valid ? (
                        <div className="contact-field-meta is-visible">
                          <p
                            id="contact-name-hint"
                            className="contact-field-hint is-invalid"
                            aria-live="polite"
                          >
                            {nameCheck.hint}
                          </p>
                        </div>
                      ) : null}
                    </div>
                    <div
                      className={`form-group contact-field${
                        showEmailStatus
                          ? emailCheck.valid
                            ? " is-valid"
                            : " is-invalid"
                          : ""
                      }`}
                    >
                      <div className="contact-field-control">
                        <input
                          type="email"
                          className="form-control"
                          name="from_email"
                          placeholder="Email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          onBlur={syncFieldsFromDom}
                          onAnimationStart={handleAutofillAnimation}
                          autoComplete="email"
                          aria-invalid={showEmailStatus && !emailCheck.valid}
                          aria-describedby="contact-email-hint"
                        />
                        <FieldFlag
                          show={showEmailStatus}
                          valid={emailCheck.valid}
                        />
                      </div>
                      {showEmailStatus && !emailCheck.valid ? (
                        <div className="contact-field-meta is-visible">
                          <p
                            id="contact-email-hint"
                            className="contact-field-hint is-invalid"
                            aria-live="polite"
                          >
                            {emailCheck.hint}
                          </p>
                        </div>
                      ) : null}
                    </div>
                    <div
                      className={`form-group contact-field${
                        showMessageStatus
                          ? messageCheck.valid
                            ? " is-valid"
                            : " is-invalid"
                          : ""
                      }`}
                    >
                      <div className="contact-field-control contact-field-control-textarea">
                        <textarea
                          id="message"
                          cols={30}
                          rows={7}
                          className="form-control"
                          name="message"
                          placeholder="Message"
                          value={message}
                          onChange={(event) =>
                            setMessage(
                              event.target.value.slice(0, MESSAGE_MAX_LENGTH)
                            )
                          }
                          maxLength={MESSAGE_MAX_LENGTH}
                          aria-invalid={showMessageStatus && !messageCheck.valid}
                          aria-describedby="contact-message-hint"
                        />
                        <FieldFlag
                          show={showMessageStatus}
                          valid={messageCheck.valid}
                        />
                      </div>
                      <div
                        className={`contact-field-meta has-count${
                          showMessageStatus && !messageCheck.valid
                            ? " is-visible"
                            : ""
                        }`}
                      >
                        <p
                          id="contact-message-hint"
                          className={`contact-field-hint${
                            showMessageStatus && !messageCheck.valid
                              ? " is-invalid"
                              : ""
                          }`}
                          aria-live="polite"
                        >
                          {showMessageStatus && !messageCheck.valid
                            ? messageCheck.hint
                            : ""}
                        </p>
                        <p
                          className={`contact-field-count${
                            showMessageStatus && !messageCheck.valid
                              ? " is-invalid"
                              : ""
                          }`}
                        >
                          {message.length}/{MESSAGE_MAX_LENGTH}
                        </p>
                      </div>
                    </div>
                    <div className="form-group contact-submit">
                      <input
                        type="submit"
                        className="btn btn-primary btn-send-message"
                        value={isSending ? "Sending..." : "Send Message"}
                        disabled={isSending}
                      />
                    </div>
                  </form>
    );
  };

  return (
    <section className="colorlib-contact" data-section="contact" id="contact">
      <div className="colorlib-narrow-content">
        <div className="row">
          <div
            className="col-md-6 col-md-offset-3 col-md-pull-3 animate-box"
            data-animate-effect="fadeInLeft"
          >
            <span className="heading-meta">Get in Touch</span>
            <h2 className="colorlib-heading">Contact</h2>
          </div>
        </div>
        <div className="row">
          <div className="col-md-5">
            <div
              className="colorlib-feature colorlib-feature-sm contact-email-feature animate-box"
              data-animate-effect="fadeInLeft"
            >
              <div className="colorlib-icon">
                <GmailIcon />
              </div>
              <div className="colorlib-text">
                <p>
                  <a href={`mailto:${cvData.profile.email}`}>
                    {cvData.profile.email}
                  </a>
                </p>
              </div>
            </div>
            <div
              className="colorlib-feature colorlib-feature-sm contact-whatsapp-feature animate-box"
              data-animate-effect="fadeInLeft"
            >
              <div className="colorlib-icon">
                <i className="icon-whatsapp" />
              </div>
              <div className="colorlib-text">
                <p>
                  <a
                    href={`https://wa.me/${phoneDigits(cvData.profile.phone)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {cvData.profile.phone}
                  </a>
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-7">
            <div className="row">
              <div
                className="col-md-12 animate-box"
                data-animate-effect="fadeInRight"
              >
                {renderFormPanel()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
